import React, { useState, useEffect } from 'react';
import { Breadcrumb } from 'antd';
import { HomeOutlined, SaveOutlined } from '@ant-design/icons';
import { Link, useParams } from 'react-router-dom';
import Bread from 'modules/layoutModule/components/Bread/Bread';
import ActivitySwitch from 'modules/layoutModule/components/ActivitySwitch/ActivitySwitch';
import {
  Form,
  Input,
  Button,
  Space,
  Skeleton,
  ConfigProvider,
  BackTop,
} from 'antd';
import { FieldData } from 'rc-field-form/lib/interface';
import { useHistory } from 'react-router-dom';
import plPL from 'antd/lib/locale/pl_PL';
import 'moment/locale/pl';
import {
  getSpecificParametersRequest,
  updateFertilizersParametersRequest,
} from '../requests/fertilizersRequests';

const FertilizersParametersEdit = (): JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const [form] = Form.useForm();
  const [fields, setFields] = useState<FieldData[]>([]);
  const [loading, toggleLoading] = useState<boolean>(false);
  const history = useHistory();
  const [isSaveButtonLoading, toggleButtonLoading] = useState(false);

  useEffect(() => {
    toggleLoading(true);

    getSpecificParametersRequest(id)
      .then((response) => {
        if (response) {
          toggleLoading(false);
          const { data } = response;
          setFields([
            {
              name: ['id'],
              value: data.id,
            },
            {
              name: ['name'],
              value: data.name,
            },
          ]);

          Object.entries(data).map((entry) => {
            form.setFieldsValue({
              [entry[0]]: entry[1],
            });
          });
        } else {
          history.push('/fertilizers-parameters');
        }
      })
      .catch((e) => {
        history.push('/fertilizers-parameters');
      });
  }, []);

  const onFinish = (values: any) => {
    toggleButtonLoading(true);
    updateFertilizersParametersRequest(values).then(() => {
      toggleButtonLoading(false);
    });
  };

  const validateMessages = {
    required: 'Proszę uzupełnić pole ${label}!',
    types: {
      email: '${label} is not a valid email!',
      number: '${label} is not a valid number!',
    },
    number: {
      range: '${label} must be between ${min} and ${max}',
    },
  };

  const updateCover = (
    e: string,
    field: string,
    coverMethod: (value: string) => void,
    coverMethodChanged: (value: boolean) => void
  ) => {
    setFields([
      {
        name: [field],
        value: e,
      },
    ]);
    coverMethod(e);
    coverMethodChanged(true);
  };

  return (
    <>
      <Bread>
        <>
          <Breadcrumb.Item>
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>Nawozy</Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to="/fertilizers-parameters">Nawozy skład</Link>
          </Breadcrumb.Item>
          <Breadcrumb.Item>
            <Link to={`/fertilizers-parameters/${id}`}>Edycja wpisu</Link>
          </Breadcrumb.Item>
        </>
      </Bread>
      <ConfigProvider locale={plPL}>
        <div className="site-layout-background">
          {loading && <Skeleton active />}
          {!loading && (
            <Form
              layout="vertical"
              fields={fields}
              onFinish={onFinish}
              form={form}
              validateMessages={validateMessages}
            >
              <Form.Item name="id" hidden>
                <Input />
              </Form.Item>
              <Form.Item
                name="name"
                label="Nazwa"
                rules={[
                  {
                    required: true,
                  },
                ]}
              >
                <Input />
              </Form.Item>
              <Form.Item name="azot_CAL" label="Azot N całkowity">
                <Input />
              </Form.Item>
              <Form.Item name="azot_AZO" label="Azot N azotanowy">
                <Input />
              </Form.Item>
              <Form.Item name="azot_AMO" label="Azot N amonowy">
                <Input />
              </Form.Item>
              <Form.Item name="azot_AMI" label="Azot N amidowy">
                <Input />
              </Form.Item>
              <Form.Item
                name="fosfor_CAL"
                label="Pięciotlenek fosforu P₂O₅ całkowity"
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="fosfor_WOD"
                label="Pięciotlenek fosforu P₂O₅ rozpuszczalny w wodzie"
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="fosfor_AMO"
                label="Pięciotlenek fosforu P₂O₅ rozpuszczalny w obojętnym roztworze cytrynianu amonu"
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="fosfor_MRO"
                label="Pięciotlenek fosforu P₂O₅ rozpuszczalny w kwasie mrówkowym"
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="potas_WOD"
                label="Tlenek potasu K₂O rozpuszczalny w wodzie"
              >
                <Input />
              </Form.Item>
              <Form.Item name="wapn_CAL" label="Tlenek wapnia CaO całkowity">
                <Input />
              </Form.Item>
              <Form.Item
                name="wapn_WOD"
                label="Tlenek wapnia CaO rozpuszczalny w wodzie"
              >
                <Input />
              </Form.Item>
              <Form.Item name="magnez_CAL" label="Tlenek magnezu MgO całkowity">
                <Input />
              </Form.Item>
              <Form.Item
                name="magnez_WOD"
                label="Tlenek magnezu MgO rozpuszczalny w wodzie"
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="siarka_CAL"
                label="Trójtlenek siarki SO₃ całkowity"
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="siarka_WOD"
                label="Trójtlenek siarki SO₃ rozpuszczalny w wodzie"
              >
                <Input />
              </Form.Item>
              <Form.Item
                name="mikro_SKL"
                label="Mikroskładniki i składniki korzystne"
              >
                <Input />
              </Form.Item>
              <Form.Item name="inhibitor" label="Inhibitor">
                <Input />
              </Form.Item>
              <Form.Item>
                <Space>
                  <Button
                    type="primary"
                    htmlType="submit"
                    icon={<SaveOutlined />}
                    loading={isSaveButtonLoading}
                  >
                    Zapisz
                  </Button>
                  <Link to="/fertilizers-parameters">
                    <Button>Powrót</Button>
                  </Link>
                </Space>
              </Form.Item>
            </Form>
          )}
          {loading && (
            <Link to="/fertilizers-parameters">
              <Button>Powrót</Button>
            </Link>
          )}
          <BackTop />
        </div>
      </ConfigProvider>
    </>
  );
};

export default FertilizersParametersEdit;
